
import {
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
  useMemo,
  useRef,
  type RefObject,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Smile, Crown, Check, Copy } from 'lucide-react';
import {
  smilieOrPawnImageCandidates,
  normalizeSmileySizePx,
  smileyStripUrl,
  xatPowerIconUrl,
} from '@/lib/xat-smilie';

export { smileyStripUrl, smilieOrPawnImageCandidates };

/** Subtítulo dos heroes escuros — mesma cor da Powerpédia (/powers). */
export const heroDescriptionClass =
  'font-poppins text-xl leading-relaxed text-blue-100/80';

export const heroDescriptionDetailClass =
  'font-poppins text-lg leading-relaxed text-blue-100/80 md:text-xl';

/** Caixa do ícone principal dos powers (gradiente orbital azul escuro). */
export const ORBITAL_ICON_BOX_CLASS = 'flex items-center justify-center rounded-2xl overflow-hidden';

export const ORBITAL_ICON_BOX_STYLE: CSSProperties = {
  background:
    'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
  boxShadow: '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70',
};

export const ORBITAL_ICON_BOX_STYLE_LG: CSSProperties = {
  ...ORBITAL_ICON_BOX_STYLE,
  boxShadow: '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70, 8px 12px 28px rgba(0,0,0,0.55)',
};

/** Asset de imagem para fundos fora do Hero principal (ex.: seção último power). */
export const IMAGE_HERO_BG = 'https://xatimg.com/image/hN0S3QZAg1gW.png';

/** Base escura + imagem + overlay — seção Powers / cards (não é o hero principal). */
export function ImageBackdropLayers({ className }: { className?: string }) {
  return (
    <div className={className} aria-hidden>
      <div className="absolute inset-0 bg-[#030023]" />
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.5]"
        style={{ backgroundImage: `url('${IMAGE_HERO_BG}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#030023]/55 via-[#000b6a]/40 to-[#030023]/58 pointer-events-none" />
    </div>
  );
}

/** Mesma paleta (#030023 / #000b6a): base escura + vídeo apenas no Hero principal. */
export function HeroBackdropLayers({ className }: { className?: string }) {
  return (
    <div className={className} aria-hidden>
      {/* Base sólida para evitar flash antes do vídeo carregar */}
      <div className="absolute inset-0 bg-[#030023]" />

      {/* Vídeo de fundo — otimizado para performance */}
      <video
        className="absolute inset-0 h-full w-full object-cover opacity-50"
        src="/ajudabg2.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        disablePictureInPicture
        style={{ willChange: 'auto', contain: 'strict' }}
      />

      {/* Overlay de cor para consistência visual */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#030023]/55 via-[#000b6a]/40 to-[#030023]/58 pointer-events-none" />
    </div>
  );
}

export function cn(...parts: (string | false | undefined)[]) {
  return parts.filter(Boolean).join(' ');
}

/** Ícone oficial de xats (`/icons/xats.svg`). */
export const XATS_ICON_SRC = '/icons/xats.svg';

export function MoXat({ className }: { className?: string }) {
  return (
    <img
      src={XATS_ICON_SRC}
      alt=""
      width={16}
      height={16}
      className={cn('inline-block h-4 w-4 shrink-0 object-contain', className)}
      draggable={false}
      decoding="async"
    />
  );
}

/** Valor de preço com ícone de xats à esquerda. */
export function XatsPriceValue({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-0 items-center justify-end gap-1.5 font-poppins text-xs font-semibold sm:text-sm',
        className
      )}
    >
      <MoXat />
      <span>{children}</span>
    </span>
  );
}

/** Badge glass — mesmo estilo das linhas da secção Informações do power. */
export const POWER_INFO_BADGE_CLASS = cn(
  'rounded-2xl border border-sky-500/25 bg-gradient-to-br from-white/[0.07] to-white/[0.02]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm'
);

/** Tamanho proporcional ao comprimento do código (uma linha, sem quebra). */
export function getPowerCodeFontSize(text: string): number {
  const len = text.length;
  if (len <= 9) return 11;
  if (len <= 12) return 10;
  if (len <= 15) return 9;
  if (len <= 18) return 8;
  if (len <= 22) return 7;
  return 6;
}

/** Mesmo contorno da caixa branca do smiley principal. */
export const borderMain = 'border border-sky-400/50';

export const glassStatRow = cn(
  'flex w-full cursor-default items-center gap-3 rounded-xl px-3.5 py-2.5 shadow-sm backdrop-blur-sm transition-all duration-300',
  borderMain,
  'bg-blue-950/40 hover:bg-blue-950/62 hover:shadow-lg hover:shadow-blue-950/35 hover:ring-1 hover:ring-sky-400/30'
);

export const smileyTileBtn = cn(
  'group relative flex w-full flex-col items-center gap-2 overflow-hidden rounded-xl p-2.5',
  'bg-white/[0.04] ring-1 ring-white/[0.07]',
  'transition-all duration-200 ease-out cursor-pointer',
  'hover:bg-white/[0.09] hover:-translate-y-px hover:ring-sky-400/35 hover:shadow-lg hover:shadow-sky-500/10'
);

export const smileyPicBox = cn(
  'flex h-11 w-11 items-center justify-center rounded-lg',
  'bg-white/95 shadow-sm ring-1 ring-black/[0.06]'
);

export const labelBadge = cn(
  'text-[9px] font-mono font-medium tracking-tight text-sky-200/50',
  'truncate max-w-full leading-none'
);

/** Estilo claro para modais / fundos brancos (alinhado ao restante do portal). */
export const smileyPicBoxLight = cn(
  'flex h-12 w-12 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm'
);

export const labelBadgeLight = cn(
  'inline-flex w-full min-w-0 max-w-full items-center justify-center rounded-full px-2.5 py-1',
  'border border-slate-200 bg-slate-100 text-[9px] font-semibold tracking-tight text-slate-700',
  'whitespace-nowrap overflow-hidden text-ellipsis'
);

export const labelBadgeDark = cn(
  'inline-flex w-full min-w-0 max-w-full items-center justify-center rounded-full px-2.5 py-1',
  'border border-sky-500/25 bg-white/[0.06] text-[11px] font-semibold leading-none text-[#a9c4ff]',
  'whitespace-nowrap overflow-hidden text-ellipsis'
);

export const smileyTileBtnLight = cn(
  'relative flex w-full min-w-0 flex-col items-center gap-2 overflow-visible rounded-xl p-3 shadow-sm transition-all duration-200',
  'border border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50 hover:shadow-md'
);

export type PowerPawn = { hat: string; name: string };

export type LatestPower = {
  id: number;
  name: string;
  description: { long: string; short: string };
  prices: { fairtrade_min: number; fairtrade_max: number; store: string };
  status: false | string;
  released: string;
  /** Tags da API (ex.: is_new, is_latest) */
  categories?: string[];
  /** Categoria/tipo quando a API envia (ex.: stickers, smilies) */
  group?: string;
  category?: string;
  type?: string;
  smilies: string[];
  pawns: PowerPawn[];
};

export type ApiPayload = { code: number; powers: LatestPower[] };

/** Smilies servidos pelo gs.xatblog.net (ícones de power e alguns smilies especiais). */
export function gsXatSmileyUrl(smileyInnerCode: string, sizePx: number = 20) {
  return xatPowerIconUrl(smileyInnerCode, sizePx);
}

export function chatSmileyCode(code: string) {
  return `(${code})`;
}

/** Categoria do power: prioriza campos da API (`group`, `type`, etc.); depois tags; depois `description.short`. */
export function resolvePowerCategoryDisplay(power: LatestPower): string {
  const x = power as LatestPower & Record<string, unknown>;
  for (const k of ['group', 'category', 'category_name', 'type', 'power_type', 'style', 'subtype', 'kind'] as const) {
    const v = x[k];
    if (typeof v === 'string' && v.trim()) {
      const t = v.trim();
      return t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ');
    }
  }
  const tags = power.categories ?? [];
  if (tags.includes('is_stickers')) return 'Stickers';
  if (tags.includes('is_smilies')) return 'Smilies';
  const short = (power.description?.short ?? '').toLowerCase();
  if (short.includes('sticker')) return 'Stickers';
  if (short.includes('smilies') || short.includes('smiley')) return 'Smilies';
  return '—';
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export function GsImage({
  code,
  className,
  imgClassName,
  sizePx = 40,
}: {
  code: string;
  className?: string;
  imgClassName?: string;
  sizePx?: number;
}) {
  const px = normalizeSmileySizePx(sizePx);
  const candidates = useMemo(() => smilieOrPawnImageCandidates(code, px), [code, px]);
  const [candidateIndex, setCandidateIndex] = useState(0);
  const broken = candidateIndex >= candidates.length;
  const src = candidates[candidateIndex];

  useEffect(() => {
    setCandidateIndex(0);
  }, [code]);

  if (broken) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-xl border border-blue-900/25 bg-white text-blue-900 text-[10px] font-poppins text-center px-1.5',
          className
        )}
      >
        {code}
      </div>
    );
  }

  return (
    <div className={className}>
      <img
        src={src}
        alt={code}
        className={imgClassName}
        loading="lazy"
        onError={() => setCandidateIndex((i) => i + 1)}
      />
    </div>
  );
}

function TooltipBubble({ arrowTop }: { arrowTop: boolean }) {
  const pill = (
    <div className={cn(
      'flex items-center gap-1.5 rounded-full px-3.5 py-1.5',
      'border border-sky-500/45 bg-[#030023]/92 backdrop-blur-md',
      'font-poppins text-[11px] font-semibold text-sky-100',
      'shadow-[0_4px_20px_rgba(0,0,0,0.5),0_0_12px_rgba(56,189,248,0.15)]'
    )}>
      <Check className="h-3 w-3 shrink-0 text-sky-400" strokeWidth={3} />
      Smilie Copiado!
    </div>
  );
  if (arrowTop) {
    return (
      <>
        {pill}
        <div className="-mt-px h-0 w-0" style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid rgba(3,0,35,0.92)' }} />
      </>
    );
  }
  return (
    <>
      <div className="-mb-px h-0 w-0" style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '6px solid rgba(3,0,35,0.92)' }} />
      {pill}
    </>
  );
}

/** Balão “Copied to clipboard”. Com `anchorRef`, usa portal + fixed (não é cortado por overflow do scroll). */
export function CopyTooltip({
  visible,
  side = 'top',
  anchorRef,
}: {
  visible: boolean;
  side?: 'top' | 'bottom';
  anchorRef?: RefObject<HTMLElement | null>;
}) {
  const top = side === 'top';
  const [fixedPos, setFixedPos] = useState<{ left: number; top: number } | null>(null);
  const portal = Boolean(anchorRef);

  useLayoutEffect(() => {
    if (!visible || !portal || !anchorRef?.current) {
      setFixedPos(null);
      return;
    }
    const el = anchorRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      setFixedPos({ left: r.left + r.width / 2, top: top ? r.top : r.bottom });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [visible, portal, anchorRef, top]);

  /* Enter: 120ms snappy. Exit: 220ms suave. */
  const animatedInner = (
    <div
      className={cn(
        'flex flex-col items-center will-change-[opacity,transform]',
        top ? 'origin-bottom' : 'origin-top',
        visible
          ? 'scale-100 opacity-100 translate-y-0 transition-[opacity,transform] duration-[120ms] ease-out'
          : top
            ? 'scale-95 opacity-0 translate-y-2 transition-[opacity,transform] duration-[220ms] ease-in'
            : 'scale-95 opacity-0 -translate-y-2 transition-[opacity,transform] duration-[220ms] ease-in'
      )}
    >
      <TooltipBubble arrowTop={top} />
    </div>
  );

  if (portal && anchorRef) {
    if (!visible || !fixedPos) return null;
    const style: CSSProperties = top
      ? {
          position: 'fixed',
          left: fixedPos.left,
          top: fixedPos.top,
          transform: 'translate(-50%, calc(-100% - 10px))',
          zIndex: 9999,
          pointerEvents: 'none',
        }
      : {
          position: 'fixed',
          left: fixedPos.left,
          top: fixedPos.top + 10,
          transform: 'translateX(-50%)',
          zIndex: 9999,
          pointerEvents: 'none',
        };
    return createPortal(
      <div className="pointer-events-none w-max" style={style} aria-live="polite">
        {animatedInner}
      </div>,
      document.body
    );
  }

  return (
    <div
      className={cn(
        'pointer-events-none absolute left-1/2 z-30 w-max -translate-x-1/2',
        top ? 'bottom-full mb-2' : 'top-full mt-2'
      )}
      aria-live="polite"
    >
      {animatedInner}
    </div>
  );
}

export function SectionLabel({
  icon: Icon,
  label,
  count,
  accent,
  variant = 'dark',
}: {
  icon: typeof Smile;
  label: string;
  count: number;
  accent: 'blue' | 'violet';
  variant?: 'dark' | 'light';
}) {
  if (variant === 'light') {
    const iconBg =
      accent === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700';
    return (
      <div className="mb-4 flex items-center gap-2.5">
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', iconBg)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="font-poppins text-sm font-bold tracking-tight text-slate-900">{label}</h3>
          <span className="font-poppins text-[11px] font-medium tabular-nums text-slate-500">{count}</span>
        </div>
      </div>
    );
  }

  const iconBg =
    accent === 'blue' ? 'bg-blue-950/55 text-sky-200' : 'bg-blue-950/55 text-violet-200';
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', iconBg)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="font-poppins text-sm font-bold tracking-tight text-white">{label}</h3>
        <span className="font-poppins text-[11px] font-medium tabular-nums text-blue-100/85">{count}</span>
      </div>
    </div>
  );
}

function GridCopyOverlay({ isCopied }: { isCopied: boolean }) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 rounded-xl bg-blue-600/90 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
      {isCopied ? (
        <>
          <Check className="h-5 w-5 text-white" />
          <span className="font-poppins text-[10px] font-bold text-white">Copiado!</span>
        </>
      ) : (
        <>
          <Copy className="h-4 w-4 text-white" />
          <span className="font-poppins text-[10px] font-bold text-white">Copiar código</span>
        </>
      )}
    </div>
  );
}

export function SmileyGridTile({
  code,
  copiedKey,
  onCopy,
  variant = 'dark',
}: {
  code: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  variant?: 'dark' | 'light';
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const key = `sm-${code}`;
  const chat = chatSmileyCode(code);
  const isCopied = copiedKey === key;

  if (variant === 'light') {
    const btnClass = smileyTileBtnLight;
    const picBox = smileyPicBoxLight;
    const badgeClass = cn(labelBadgeLight, 'font-poppins');
    return (
      <button ref={ref} type="button" onClick={() => onCopy(key, chat)} className={btnClass} title={`Copiar ${chat}`}>
        <CopyTooltip visible={isCopied} side="bottom" anchorRef={ref} />
        <div className={picBox}>
          <GsImage code={code} className="flex h-11 w-11 items-center justify-center" imgClassName="max-h-[40px] max-w-[40px] object-contain" />
        </div>
        <span className={badgeClass}>{code}</span>
      </button>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onCopy(key, chat)}
      title={`Copiar ${chat}`}
      className="group relative flex w-full flex-col items-center gap-2 overflow-hidden rounded-xl p-2.5 font-poppins transition-all duration-200 ease-out cursor-pointer select-none"
      style={{
        background: 'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
        boxShadow: '0 0 0 1px #0f1e70',
      }}
    >
      <GridCopyOverlay isCopied={isCopied} />

      <div className="relative flex h-11 w-11 items-center justify-center">
        <GsImage
          code={code}
          className="flex h-full w-full items-center justify-center"
          imgClassName="max-h-[38px] max-w-[38px] object-contain"
        />
      </div>

      <span className={cn(labelBadgeDark, 'relative font-poppins')}>{code}</span>
    </button>
  );
}

export function PawnGridTile({
  pawn,
  index,
  copiedKey,
  onCopy,
  variant = 'dark',
}: {
  pawn: PowerPawn;
  index: number;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  variant?: 'dark' | 'light';
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const pKey = `pawn-${index}-${pawn.hat}`;
  const isCopied = copiedKey === pKey;

  if (variant === 'light') {
    const btnClass = smileyTileBtnLight;
    const picBox = smileyPicBoxLight;
    const badgeClass = cn(labelBadgeLight, 'font-poppins');
    return (
      <button ref={ref} type="button" onClick={() => onCopy(pKey, pawn.hat)} className={btnClass} title="Copiar código">
        <CopyTooltip visible={isCopied} side="bottom" anchorRef={ref} />
        <div className={picBox}>
          <GsImage code={pawn.name} className="flex h-11 w-11 items-center justify-center" imgClassName="max-h-[40px] max-w-[40px] object-contain" />
        </div>
        <span className={badgeClass}>{pawn.hat}</span>
      </button>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onCopy(pKey, pawn.hat)}
      title="Copiar código do peão"
      className="group relative flex w-full flex-col items-center gap-2 overflow-hidden rounded-xl p-2.5 font-poppins transition-all duration-200 ease-out cursor-pointer select-none"
      style={{
        background: 'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
        boxShadow: '0 0 0 1px #0f1e70',
      }}
    >
      <GridCopyOverlay isCopied={isCopied} />

      <div className="relative flex h-11 w-11 items-center justify-center">
        <GsImage
          code={pawn.name}
          className="flex h-full w-full items-center justify-center"
          imgClassName="max-h-[38px] max-w-[38px] object-contain"
        />
      </div>

      <span className={cn(labelBadgeDark, 'relative font-poppins')}>{pawn.hat}</span>
    </button>
  );
}

export const scrollRowClass =
  'flex gap-2.5 sm:gap-3 overflow-x-auto pb-2 pt-0.5 -mx-1 px-1 scroll-smooth [scrollbar-width:thin] [scrollbar-color:rgba(37,99,235,0.35)_transparent]';
